import React, { lazy, Suspense, useEffect } from 'react';
import { Redirect, useHistory, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { ROLES } from '../utils/constants';
import { privateRoutes } from '../utils/rules';
import { AGE_GATE_LEGAL_PATHS } from '../utils/ageGateState';
import ApplicationLoader from '../common/ApplicationLoader';

const PublicHomepageShell = lazy(() => import(
    /* webpackChunkName: "public-homepage" */ './PublicHomepageShell'
));
const PublicShell = lazy(() => import(
    /* webpackChunkName: "public-shell" */ './PublicShell'
));
const AuthenticatedShell = lazy(() => import(
    /* webpackChunkName: "authenticated-shell" */ './AuthenticatedShell'
));
const AdminShell = lazy(() => import(
    /* webpackChunkName: "admin-shell" */ './AdminShell'
));
const ManagerShell = lazy(() => import(
    /* webpackChunkName: "manager-shell" */ './ManagerShell'
));

const PUBLIC_CONTENT_PATHS = [
    '/faq',
    '/blog',
    '/blog/why-private-sharing-matters',
    '/blog/every-life-has-a-story-worth-celebrating',
    '/blog/weddings-families-and-moments-worth-protecting',
    '/live-test',
];

const storedUser = () => {
    try {
        return JSON.parse(localStorage.getItem('user')) || null;
    } catch (error) {
        return null;
    }
};

const AppRoute = () => {
    const location = useLocation();
    const history = useHistory();
    const reduxUser = useSelector((state) => state.user.user);
    const userInformation = reduxUser && Object.keys(reduxUser).length > 0
        ? reduxUser
        : storedUser();
    const storedUserId = localStorage.getItem('loggedin_user');
    const userID = userInformation?.id || storedUserId;
    const isAuthenticated = Boolean(userID || userInformation);
    const isManager = userInformation
        && [ROLES.MANAGER, ROLES.COMPLIANCE_REVIEWER].includes(Number(userInformation.role));
    const isManagerPath = location.pathname.indexOf('/administration/manager') === 0;
    const isAdminPath = location.pathname.indexOf('/administration') === 0;
    const isComplianceRoute = AGE_GATE_LEGAL_PATHS.includes(location.pathname);
    const isCelebrationRoute = location.pathname.indexOf('/celebrate/') === 0;
    const isPublicContentRoute = PUBLIC_CONTENT_PATHS.includes(location.pathname);
    const isCreatorSecureLoginRoute = location.pathname === '/creator/secure-login';

    useEffect(() => {
        const root = document.getElementById('chat_cont');
        let pathname = location.pathname.replace(/^\//, '').split('?')[0];

        if (!pathname && userInformation) pathname = 'home';
        if (root) {
            root.className = '';
            root.classList.add(pathname ? `page-${pathname.replace(/\s/g, '')}` : 'page');
        }

        const mainApp = document.querySelector('.main-app');
        mainApp?.classList.toggle('is-guest', !userInformation);
        mainApp?.classList.toggle('is-login', Boolean(userInformation));
        mainApp?.classList.toggle('login-pu', Number(userInformation?.role) === ROLES.PU);
        mainApp?.classList.toggle('login-su', Number(userInformation?.role) === ROLES.SU);

        const firstSegment = location.pathname.split('/').filter(Boolean)[0] || '';
        if (privateRoutes.includes(firstSegment) && !isAuthenticated && !isCreatorSecureLoginRoute) {
            const intendedPath = `${location.pathname}${location.search || ''}${location.hash || ''}`;
            localStorage.setItem('intended', intendedPath);
            window.location = `/login?redirect=${encodeURIComponent(intendedPath)}`;
            return;
        }

        if (isAuthenticated && isManager && !isManagerPath && !isComplianceRoute) {
            history.replace('/administration/manager');
            return;
        }

        if (
            isAuthenticated
            && userInformation
            && ['/register', '/login'].includes(location.pathname)
        ) {
            const redirectPath = new URLSearchParams(location.search).get('redirect');
            const targetPath = isManager
                ? '/administration/manager'
                : (
                    redirectPath
                    && redirectPath.startsWith('/')
                    && !redirectPath.startsWith('//')
                    && !['/login', '/register'].includes(redirectPath)
                        ? redirectPath
                        : '/home'
                );
            history.replace(targetPath);
        }
    }, [
        history,
        isAuthenticated,
        isComplianceRoute,
        isCreatorSecureLoginRoute,
        isManager,
        isManagerPath,
        location.hash,
        location.pathname,
        location.search,
        userInformation,
    ]);

    let shell;

    if (!isAuthenticated && location.pathname === '/') {
        shell = <PublicHomepageShell />;
    } else if (
        !isAuthenticated
        || isComplianceRoute
        || isCelebrationRoute
        || isPublicContentRoute
        || isCreatorSecureLoginRoute
        || location.pathname === '/404'
    ) {
        shell = <PublicShell />;
    } else if (isManager) {
        shell = isManagerPath ? <ManagerShell /> : <Redirect to="/administration/manager" />;
    } else if (isAdminPath) {
        shell = Number(userInformation?.role) === ROLES.ADMIN ? <AdminShell /> : null;
    } else {
        shell = <AuthenticatedShell />;
    }

    return (
        <Suspense fallback={<ApplicationLoader />}>
            {shell}
        </Suspense>
    );
};

export default AppRoute;
